import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ApiError } from "@/app/api";
import { notify } from "@/app/notify";
import { ReadonlyBlock } from "@/app/ReadonlyBlock";
import { Alert, Button, Drawer, EmptyState, formatTime, KeyValue, SectionTitle, useToast } from "@/ui";
import { LevelPill, StatusPill } from "./NotifyPills";

const time = (v: string | null | undefined) => (v ? formatTime(v) : "—");

/** 事件详情抽屉：基本信息、正文、实际发出的 JSON（sent_payload，最近一次）、最后错误；failed 与 skipped 可重新发送（docs/04 第 15 节）。
 * 重新发送是条件更新：别处已经改了状态时 409，提示并刷新，不重试。 */
export function NotifyEventDetail({ id, onClose }: { id: number; onClose: () => void }) {
  const q = useQuery({ queryKey: ["notify-events", "detail", id], queryFn: () => notify.event(id) });
  const e = q.data;
  const qc = useQueryClient();
  const toast = useToast();
  const refresh = () => { void qc.invalidateQueries({ queryKey: ["notify-events"] }); };
  const resend = useMutation({
    mutationFn: () => notify.resend(id),
    onSuccess: () => { refresh(); toast("已重新排队，下一轮投递", { tone: "success" }); },
    // 409：别处已经改了状态（例如 cron 刚重新投递），刷新取最新状态，不自动重试
    onError: (err) => { if (err instanceof ApiError && err.status === 409) { refresh(); toast("事件状态已变化，已刷新", { tone: "danger" }); } },
  });
  const failure = resend.error && !(resend.error instanceof ApiError && resend.error.status === 409) ? resend.error.message : null;
  return (
    <Drawer open title="事件详情" onClose={onClose}
      footer={<><Button onClick={onClose}>关闭</Button>{e && (e.status === "failed" || e.status === "skipped") && <Button variant="primary" disabled={resend.isPending} onClick={() => resend.mutate()}>重新发送</Button>}</>}>
      {q.error && <Alert tone="danger" title="加载失败">{q.error.message}</Alert>}
      {!e && !q.error && <EmptyState title="加载中" />}
      {e && (
        <div className="ax-detail">
          {failure && <Alert tone="danger" title="重新发送失败">{failure}</Alert>}
          <KeyValue items={[
            { label: "标题", value: e.title },
            { label: "类型", value: e.type, mono: true },
            { label: "等级", value: <LevelPill level={e.level} /> },
            { label: "状态", value: <StatusPill status={e.status} /> },
            { label: "发生时间", value: time(e.occurred_at), mono: true },
            { label: "操作人", value: e.actor || "—" },
            { label: "对象", value: e.object ? `${e.object.kind} ${e.object.name || e.object.id}` : "—" },
            { label: "尝试次数", value: String(e.attempts), mono: true },
            { label: "响应码", value: e.last_status_code ?? "—", mono: true },
            { label: "下次发送", value: time(e.next_at), mono: true },
            { label: "跳过原因", value: e.skip_reason || "—" },
            { label: "报文 id", value: e.event_id, mono: true },
          ]} />
          <section className="ax-section"><SectionTitle>正文</SectionTitle><ReadonlyBlock label="正文" value={e.body} /></section>
          {e.last_error && <section className="ax-section"><SectionTitle>最后错误</SectionTitle><ReadonlyBlock label="最后错误" value={e.last_error} /></section>}
          <section className="ax-section">
            <SectionTitle>实际发出的 JSON</SectionTitle>
            {e.sent_payload == null ? <p className="ax-secondary">还没有发出过。</p> : <ReadonlyBlock label="实际发出的 JSON" value={e.sent_payload} rows={12} />}
          </section>
        </div>
      )}
    </Drawer>
  );
}
